"use client";

import { createContext, useContext, useEffect, useState } from "react";
import { type Smplr, loadSmplrJs } from "@smplrspace/smplr-loader";

/** QueryClient type from Smplr SDK (created at runtime from client.QueryClient) */
type SmplrQueryClient = InstanceType<Smplr["QueryClient"]> | null;

export type SmplrQueryClientStatus =
  | "ready"
  | "no_client"
  | "no_org_id"
  | "no_token"
  | "init_failed";

type SmplrContextType = {
  client: Smplr | null;
  /** QueryClient for fetching floors/rooms from space. Requires NEXT_PUBLIC_SMPLR_ORGANIZATION_ID. */
  queryClient: SmplrQueryClient;
  /** Why queryClient is null (for UI / debugging). */
  queryClientStatus: SmplrQueryClientStatus;
  isLoading: boolean;
  error: Error | null;
};

const SmplrContext = createContext<SmplrContextType>({
  client: null,
  queryClient: null,
  queryClientStatus: "no_client",
  isLoading: true,
  error: null,
});

/**
 * Loads Smplr.js from CDN and provides it via context.
 * Use "umd" bundle for Next.js compatibility (esm dynamic imports can fail).
 * QueryClient is created when client is ready and NEXT_PUBLIC_SMPLR_ORGANIZATION_ID is set (needed for fetching floors/areas).
 *
 * @see https://docs.smplrspace.com/
 * @see https://gist.github.com/sean-ocallahan/48bed44902c0bc7eda6de42af14d9d25
 */
export function SmplrProvider({ children }: { children: React.ReactNode }) {
  const [client, setClient] = useState<Smplr | null>(null);
  const [queryClient, setQueryClient] = useState<SmplrQueryClient>(null);
  const [queryClientStatus, setQueryClientStatus] = useState<SmplrQueryClientStatus>("no_client");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    loadSmplrJs("umd")
      .then((smplr) => {
        setClient(smplr);
        setError(null);
      })
      .catch((err) => {
        console.error("Failed to load Smplr Space SDK", err);
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!client) {
      setQueryClient(null);
      setQueryClientStatus("no_client");
      return;
    }
    const orgId = process.env.NEXT_PUBLIC_SMPLR_ORGANIZATION_ID;
    const token = process.env.NEXT_PUBLIC_SMPLR_CLIENT_TOKEN;
    if (!orgId) {
      setQueryClient(null);
      setQueryClientStatus("no_org_id");
      return;
    }
    if (!token) {
      setQueryClient(null);
      setQueryClientStatus("no_token");
      return;
    }
    try {
      const qc = new client.QueryClient({ organizationId: orgId, clientToken: token });
      setQueryClient(qc as SmplrQueryClient);
      setQueryClientStatus("ready");
    } catch (e) {
      console.warn("Smplr QueryClient init failed", e);
      setQueryClient(null);
      setQueryClientStatus("init_failed");
    }
  }, [client]);

  return (
    <SmplrContext.Provider value={{ client, queryClient, queryClientStatus, isLoading, error }}>
      {children}
    </SmplrContext.Provider>
  );
}

export function useSmplr() {
  const ctx = useContext(SmplrContext);
  if (!ctx) {
    throw new Error("useSmplr must be used within SmplrProvider");
  }
  return ctx;
}
